import {
  BehaviorSubject,
  catchError,
  delay,
  filter,
  from,
  mergeMap,
  Observable,
  of,
} from 'rxjs';
import { Dependencies } from '../../../../configurations/dependencies/dependencies.interface';
import { Action, AnyAction } from '../../../state/action.type';
import { AppState } from '../../../state/app.state';
import {
  changePassword,
  changePasswordFailure,
  changePasswordSuccess,
} from '../../../state/authentication/authentication.action';
import {
  ChangePassword,
  CHANGE_PASSWORD,
} from '../../../state/authentication/authentication.action.type';
import { store } from '../../../state/store';
import { AUTH_USECASE_ERROR_MESSAGES } from '../../errors/usecases.errorMessages/authenticationUsecase.error.messages';

const DEFAULT_RETRY_DELAY = 300;

export const changePasswordUsecase = (
  action$: Observable<Action>,
  _state$: BehaviorSubject<AppState>,
  dependencies: Dependencies,
): Observable<Action> =>
  action$.pipe(
    filter(
      (action: AnyAction): action is ChangePassword =>
        action.type === CHANGE_PASSWORD,
    ),
    mergeMap((action: ChangePassword) => {
      const { oldPassword, newPassword, token, retry } = action.payload;
      const userEmail = store.getState().user.profile?.email;

      if (!userEmail) {
        return of(changePasswordFailure('User email not found'));
      }

      return from(
        // Verify old password to ensure user is authorized
        dependencies.oicdAuthenticatorGateway.verifyOldPassword(
          userEmail,
          oldPassword,
        ),
      ).pipe(
        // Change new password in backend
        mergeMap(() =>
          from(
            dependencies.backendAuthenticatorGateway.changePassword(
              token,
              newPassword,
            ),
          ),
        ),
        mergeMap((newToken: string) => {
          return [changePasswordSuccess(newToken)];
        }),
        catchError((error: Error) => {
          console.warn('Change Password Failed:', error.message);
          const remainingRetries = retry ? retry - 1 : 0;

          if (remainingRetries) {
            return of(
              changePassword({
                oldPassword,
                newPassword,
                token,
                retry: remainingRetries,
              }),
            ).pipe(delay(DEFAULT_RETRY_DELAY));
          }
          return of(
            changePasswordFailure(
              AUTH_USECASE_ERROR_MESSAGES.PASSWORD_CHANGE_FAILED,
            ),
          );
        }),
      );
    }),
  );
